// ANETT HORVATH — Enhanced Media Library
// All images/videos loaded from here. No static imports.

import type { SupportedLang } from '../config/house.config';

export interface MediaItem {
  id: string;
  file_url: string;
  type: 'image' | 'video' | 'editorial' | 'product' | 'symbol' | 'background' | 'brand';
  label: string;
  language: 'global' | SupportedLang;
  usage_refs: string[]; // content_block keys referencing this
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

const ML_KEY = 'ah_media_library';
const uid = () => crypto.randomUUID();
const now = () => new Date().toISOString();

function load(): MediaItem[] {
  try { const r = localStorage.getItem(ML_KEY); return r ? JSON.parse(r) : seedMedia(); } catch { return seedMedia(); }
}
function save(items: MediaItem[]) { localStorage.setItem(ML_KEY, JSON.stringify(items)); }

function seedMedia(): MediaItem[] {
  const ts = now();
  const m = (url: string, type: MediaItem['type'], label: string): MediaItem => ({
    id: uid(), file_url: url, type, label, language: 'global', usage_refs: [], created_at: ts, updated_at: ts, deleted_at: null
  });
  const items: MediaItem[] = [
    m('/assets/brand-editorial-spray.png', 'editorial', 'Hero — Perfume Spray Editorial'),
    m('/assets/brand-aethera-moodboard.png', 'brand', 'AETHERA — Brand Moodboard'),
    m('/assets/brand-aethera-packaging.png', 'product', 'AETHERA — Extrait Packaging'),
    m('/assets/brand-moonoflife-product.png', 'product', 'Moon of Life — Product Line'),
    m('/assets/brand-perfume-editorial.png', 'editorial', 'Perfume Editorial'),
    m('/assets/brand-golden-collection.png', 'editorial', 'Golden Collection'),
    m('/assets/brand-luxury-detail.png', 'editorial', 'Luxury Detail'),
    m('/assets/brand-product-closeup.png', 'product', 'Product Closeup'),
    m('/assets/brand-artistic-still.png', 'editorial', 'Artistic Still Life'),
    m('/assets/brand-editorial-detail.png', 'editorial', 'Editorial Detail'),
    m('/assets/brand-editorial-mood.png', 'editorial', 'Editorial Mood'),
    m('/assets/brand-product-lifestyle.png', 'product', 'Product Lifestyle'),
    m('/assets/brand-visual-identity.png', 'brand', 'Visual Identity'),
    m('/assets/ah-monogram.png', 'symbol', 'AH Monogram'),
    m('/assets/house-symbol.png', 'symbol', 'House Symbol'),
  ];
  save(items);
  return items;
}

// Cache-busted URL
export function mediaUrl(item: MediaItem): string {
  if (!item || !item.file_url) return '';
  return item.file_url + '?v=' + encodeURIComponent(item.updated_at || '');
}

export function getAllMedia(): MediaItem[] { return load().filter(m => !m.deleted_at); }
export function getAllMediaIncDeleted(): MediaItem[] { return load(); }
export function getMediaById(id: string): MediaItem | undefined { return load().find(m => m.id === id); }

export function getMediaByLabel(label: string): MediaItem | undefined {
  return load().find(m => m.label === label && !m.deleted_at);
}

export function upsertMedia(id: string | null, data: Partial<MediaItem>): MediaItem {
  const items = load();
  const ts = now();
  if (id) {
    const idx = items.findIndex(m => m.id === id);
    if (idx >= 0) {
      items[idx] = { ...items[idx], ...data, updated_at: ts };
      save(items);
      return items[idx];
    }
  }
  const item: MediaItem = {
    id: uid(), file_url: data.file_url || '', type: data.type || 'image',
    label: data.label || '', language: data.language || 'global', usage_refs: [],
    created_at: ts, updated_at: ts, deleted_at: null,
  };
  items.push(item);
  save(items);
  return item;
}

export function softDeleteMedia(id: string) {
  const items = load();
  const idx = items.findIndex(m => m.id === id);
  if (idx >= 0) { items[idx].deleted_at = now(); save(items); }
}

export function restoreMedia(id: string) {
  const items = load();
  const idx = items.findIndex(m => m.id === id);
  if (idx >= 0) { items[idx].deleted_at = null; save(items); }
}

// Fallback placeholder for broken media
export const FALLBACK_IMAGE = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#F4F0E8" width="400" height="300"/><text x="200" y="150" text-anchor="middle" fill="#1C1C1C" opacity="0.15" font-family="serif" font-size="18">AH</text></svg>');
